import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR } from './lib';
import { AlternativePicker, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock } from './SharedBlocks';

const mono = "ui-monospace, 'SF Mono', Menlo, monospace";

const NotionLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied,
  } = ctx;
  const cust = quote.customerData || {};
  const headBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: theme.radius, background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, color: theme.textSecondary, cursor: 'pointer', fontSize: 12 };

  const Row = ({ service, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '9px 0', opacity: isOpt && !on ? 0.5 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            {isOpt
              ? <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)} style={{ width: 16, height: 16, marginTop: 2, accentColor: theme.primary, cursor: 'pointer' }} />
              : <span style={{ fontSize: 14, marginTop: 1 }}>☑️</span>}
            <div>
              <p style={{ fontSize: 15, color: theme.textPrimary, margin: 0, fontWeight: 500 }}>{service.name}</p>
              {service.description && <p style={{ fontSize: 13, color: theme.textSecondary, marginTop: 3, lineHeight: 1.6 }}>{service.description}</p>}
              <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
            </div>
          </div>
          <span style={{ fontFamily: mono, fontSize: 13, color: theme.textSecondary, whiteSpace: 'nowrap' }}>{formatEUR(lineTotal(service))}{suffix ? ` ${suffix}` : ''}</span>
        </div>
      </div>
    );
  };
  const Group = ({ emoji, title, services, suffix }) => services.length ? (
    <div>
      <h3 style={{ fontSize: 17, fontWeight: 600, color: theme.textPrimary, margin: '18px 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><span>{emoji}</span>{title}</h3>
      <div style={{ borderTop: `1px solid ${theme.surfaceBorder}` }}>{services.map((s, i) => <Row key={s.id || i} service={s} suffix={suffix} />)}</div>
    </div>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 70px' }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px', maxWidth: 760, margin: '0 auto' }}>
        <button onClick={handleSaveAsPdf} style={headBtn}><Download className="w-4 h-4" />PDF</button>
        <button onClick={handleCopyLink} style={headBtn}>{linkCopied ? <Check className="w-4 h-4" style={{ color: '#0f7b6c' }} /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 28px' }}>
        {/* Doc header */}
        <div style={{ padding: '20px 0 8px' }}>
          {company?.logo
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="Logo" style={{ height: 30, marginBottom: 16, objectFit: 'contain' }} />
            : <div style={{ fontSize: 40, marginBottom: 6 }}>📄</div>}
          <h1 style={{ fontSize: 34, fontWeight: 700, color: theme.textPrimary, margin: 0, lineHeight: 1.2, letterSpacing: '-0.5px' }}>
            {quote.heroHeadline || `Angebot — ${cust.company || ''}`}
          </h1>
          <p style={{ fontFamily: mono, fontSize: 12, color: theme.textMuted, marginTop: 10 }}>
            {quote.quoteNumber} · {new Date(quote.createdAt).toLocaleDateString('de-DE')} · {cust.name || ''}
          </p>
        </div>

        {quote.quoteIntro && <p style={{ fontSize: 15, color: theme.textPrimary, lineHeight: 1.7, marginTop: 14, whiteSpace: 'pre-wrap' }}>{quote.quoteIntro}</p>}

        {quote.customerArguments && (
          <div style={{ background: '#f7f6f3', borderLeft: `3px solid ${theme.primary}`, borderRadius: theme.radius, padding: '12px 14px', margin: '16px 0', fontSize: 14, color: theme.textPrimary, lineHeight: 1.7 }}>
            <span style={{ marginRight: 6 }}>💡</span>
            <span dangerouslySetInnerHTML={{ __html: quote.customerArguments }} style={{ display: 'inline' }} />
          </div>
        )}
        {quote.additionalInfo && (
          <div style={{ background: '#f7f6f3', borderRadius: theme.radius, padding: '12px 14px', margin: '12px 0', fontSize: 14, color: theme.textSecondary, lineHeight: 1.7 }}>
            <span style={{ marginRight: 6 }}>ℹ️</span>{quote.additionalInfo}
          </div>
        )}
        {quote.videoUrl && <div className="no-print" style={{ margin: '16px 0' }}><video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius }} /></div>}

        <Group emoji="🎯" title="Einmalige Leistungen" services={einmaligServices} suffix="" />
        <Group emoji="🔄" title="Monatliche Leistungen" services={monatlichServices} suffix="/ Mo" />
        <Group emoji="📅" title="Jährliche Leistungen" services={jaehrlichServices} suffix="/ Jahr" />

        <div style={{ background: '#f7f6f3', borderRadius: theme.radius, padding: '14px 16px', margin: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: theme.textSecondary }}>💰 Gesamt inkl. MwSt.</span>
          <span style={{ fontFamily: mono, fontSize: 20, fontWeight: 600, color: theme.textPrimary }}>{formatEUR(einmaligTotals.brutto)}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TermsBlock quote={quote} company={company} theme={theme} />
          <AcceptanceBlock ctx={ctx} theme={theme} />
          <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FeedbackBlock viewHash={viewHash} theme={theme} />
            <QuestionBlock viewHash={viewHash} theme={theme} />
          </div>
          <FooterBlock company={company} theme={theme} />
        </div>
      </div>
    </div>
  );
};

export default NotionLayout;
